import React from "react";
import styled from "styled-components";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import moment from "moment";
import { RouteComponentProps, withRouter } from "react-router";
import { X } from "@sonder/ui/source/icons";
import {
  BackgroundVariant,
  CircleButton,
} from "@sonder/ui/source/styled/fragments/CircleButton";
import { PanelNav } from "@sonder/ui/source/styled/fragments/PanelNav";
import { PanelNavContent } from "@sonder/ui/source/styled/layouts/PanelNavContent";
import { Button } from "@sonder/ui/source/styled/primitives/Button";
import {
  SchedulerColors,
  SchedulerTextColors,
  colors,
} from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useStore } from "auth/store";
import { TaskData } from "core/queries/GetTask";
import { mapTaskDataToProps } from "core/queries/mappers";
import {
  AdditionalOptionsMenu,
  AdditionalOptionsMenuTrigger,
} from "tasks/components/AdditionalOptionsMenu";
import { CompleteTaskButton } from "tasks/components/CompleteTaskButton";
import { canViewBackToTasks } from "tasks/helpers";
import { useGetAdditionalOptions } from "tasks/hooks/useGetAdditionalOptions";
import { FilterStatus } from "typings/globalGQLTypes";

const StyledActions = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledNavContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledBackText = styled.strong`
  color: ${props => props.color};
  margin-left: 0.3rem;
  text-decoration: underline;
`;

const Separator = styled.strong`
  margin: 0 0.4rem;
`;

interface TaskProps {
  statusLabel: string;
  primaryColor: string;
  secondaryColor: string;
  backgroundVariant: BackgroundVariant;
  linkColor: string;
}
const mapStatusToTaskProps = (status: string | null): TaskProps => {
  switch (status) {
    case "deleted":
    case "canceled":
      return {
        statusLabel: "Archived",
        backgroundVariant: "light",
        primaryColor: SchedulerTextColors.archived,
        secondaryColor: SchedulerColors.archived,
        linkColor: colors.blueT,
      };

    case "arriving":
    case "enroute":
      return {
        statusLabel: "En route",
        backgroundVariant: "medium",
        primaryColor: colors.white,
        secondaryColor: colors.gray6,
        linkColor: colors.white,
      };

    case "complete":
      return {
        statusLabel: "Completed",
        backgroundVariant: "light",
        primaryColor: SchedulerTextColors.complete,
        secondaryColor: SchedulerColors.complete,
        linkColor: colors.black,
      };

    case "inprogress":
      return {
        statusLabel: "In progress",
        backgroundVariant: "dark",
        primaryColor: SchedulerTextColors.inprogress,
        secondaryColor: SchedulerColors.inprogress,
        linkColor: colors.white,
      };

    case "waiting":
      return {
        statusLabel: "Waiting",
        backgroundVariant: "light",
        primaryColor: SchedulerTextColors.waiting,
        secondaryColor: SchedulerColors.waiting,
        linkColor: colors.blueT,
      };

    case "unassigned":
      return {
        statusLabel: "Not Assigned",
        backgroundVariant: "light",
        primaryColor: SchedulerTextColors.unassigned,
        secondaryColor: SchedulerColors.unassigned,
        linkColor: colors.black,
      };

    case "ready":
    default:
      return {
        statusLabel: "Not Started",
        backgroundVariant: "dark",
        primaryColor: SchedulerTextColors.ready,
        secondaryColor: SchedulerColors.ready,
        linkColor: colors.white,
      };
  }
};

interface NavLoadingProps extends RouteComponentProps {
  message: string;
  onClose?: (event: React.MouseEvent) => void;
}
export const NavLoading = withRouter(
  ({ message, onClose }: NavLoadingProps) => (
    <PanelNav childRight>
      <PanelNavContent>{message}</PanelNavContent>
      {onClose && (
        <CircleButton backgroundVariant="dark" onClick={onClose}>
          <X />
        </CircleButton>
      )}
    </PanelNav>
  ),
);

interface NavProps extends RouteComponentProps<{ taskId: string }> {
  task: TaskData;
  onClose?: (event: React.MouseEvent) => void;
}
export const NavSimple = ({ match, task, onClose, location }: NavProps) => {
  const { track } = useSegment();
  const {
    state: { user },
  } = useStore();
  const additionalOptions = useGetAdditionalOptions(task);
  const isAssigned = !!task?.assignee?.id || (task?.assignees || []).length > 0;

  const status =
    task.status === FilterStatus.ready && !isAssigned
      ? "unassigned"
      : task.status;

  const {
    statusLabel,
    backgroundVariant,
    primaryColor,
    secondaryColor,
    linkColor,
  } = mapStatusToTaskProps(status);

  const redirectToTasks = () => {
    const taskId = match.params.taskId;
    window.location.replace(`/tasks/${taskId}`);
  };

  return (
    <PanelNav
      childRight
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
    >
      <PanelNavContent>
        <StyledNavContent>
          {canViewBackToTasks(user, location.pathname) && (
            <Button onClick={redirectToTasks}>
              <KeyboardBackspaceIcon style={{ color: linkColor }} />
              <StyledBackText color={linkColor}>Back to Tasks</StyledBackText>
              <Separator>|</Separator>
            </Button>
          )}
          <strong>{statusLabel}:</strong>&nbsp;Created{" "}
          {moment(task.createdAt).fromNow()}
        </StyledNavContent>
      </PanelNavContent>
      <StyledActions>
        <CompleteTaskButton
          backgroundVariant={backgroundVariant}
          description={task.name || "(No description)"}
          eventName="task_panel"
          task={mapTaskDataToProps(task)}
        />
        {additionalOptions.length > 0 && (
          <AdditionalOptionsMenu
            additionalOptions={additionalOptions}
            eventName="task_panel"
            renderTrigger={() => (
              <AdditionalOptionsMenuTrigger
                backgroundVariant={backgroundVariant}
              />
            )}
            status={task.status}
          />
        )}
        {onClose && (
          <CircleButton
            backgroundVariant={backgroundVariant}
            onClick={event => {
              track({
                eventName: "task_panel",
                action: "click",
                element: "close_panel_button",
                props: { taskId: match.params.taskId },
              });
              onClose(event);
            }}
          >
            <X />
          </CircleButton>
        )}
      </StyledActions>
    </PanelNav>
  );
};

export const Nav = withRouter(NavSimple);
